import React, { Component } from "react";
import { RoutedViewProps } from "@my-dld/utils/UrlUtils";
import Strings from "@my-dld/assets/strings";
import Context from "../../Context";
import { ProjectProperty } from "@eres/ejari-api/Entity/LeasedProperties";
import * as AddPropertyAPI from "../../api/api";
import { showErrorMessage } from "@my-dld/utils/UIUtils";
import { getParticipantID } from "@10x/sso";
import PageTitle from "@my-dld/components/PageTitle";
import { DashboardBCItem } from "@my-dld/contexts/PageContext";
import { updateProperties, updateProperty } from "../../Utils";
import { navigateWithInParent } from "@my-dld/utils/StoreUtils";
import PropertiesListView from "../components/PropertiesListView";
import PropertiesGridView from "../components/PropertiesGridView";
import ListGridDataView from "@my-dld/components/DataView/ListGridDataView";
import { ViewTypes } from "../../api/entities";
import { EjariPropertyType } from "@eres/ejari-api/Entity";
import { showEjariDetails } from "../../NavigationUtils";
import { isEmptyStringOrNull } from "@eres/core/dist/StringUtils";
import { BUILDING_AND_UNIT, VILLA } from "./AddProperty";

interface State {
	viewType: number;
	isLoading: boolean;
	allProperties: ProjectProperty[];
	properties: ProjectProperty[];
}

class Properties extends Component<RoutedViewProps, State> {
	static contextType = Context;
	context!: React.ContextType<typeof Context>;

	constructor( props: RoutedViewProps ) {
		super( props );
		this.state = {
			isLoading: true,
			viewType: ViewTypes.ALL,
			allProperties: [],
			properties: [],
		};
	}

	componentDidMount() {
		const { searchParams, properties } = this.context;
		if ( properties ) {
			this.setState( {
				isLoading: false,
				viewType: ViewTypes.ALL,
				allProperties: properties,
				properties: properties,
			} );
		} else if ( searchParams?.landPropertyId ) {
			this.loadProperties( searchParams.landPropertyId );
		}
	}

	render() {
		const { isLoading, viewType, properties } = this.state;
		return <>
			<PageTitle title={ Strings.add_your_property } paths={ [ DashboardBCItem ] }/>
			<ListGridDataView
				key={ "" + viewType }
				isLoading={ isLoading }
				dataAvailable={ properties && properties.length > 0 }
				title={ this.getTitle() }
				renderListView={ this.renderListView }
				renderGridView={ this.renderGridView }
				totalCount={ properties.length }
				hidePageTitle={ true }>
				<>
					<div className="transformer-tabs tabs-wrapper">
						<div className="btn-group btn-toggle btn-custom-toggle">
							<button
								className={ `btn btn-sm ${ viewType === ViewTypes.ALL ? "active" : "" }` }
								onClick={ () => this.onChangeViewType( ViewTypes.ALL ) }
								type="button">{ Strings.all }</button>
							<button
								className={ `btn btn-sm ${ viewType === ViewTypes.BUILDINGS ? "active" : "" }` }
								onClick={ () => this.onChangeViewType( ViewTypes.BUILDINGS ) }
								type="button">{ Strings.add_property_buildings }</button>
							<button
								className={ `btn btn-sm ${ viewType === ViewTypes.VILLAS ? "active" : "" }` }
								onClick={ () => this.onChangeViewType( ViewTypes.VILLAS ) }
								type="button">{ Strings.add_property_villas }</button>
						</div>
						<button
							type="button"
							className="btn btn_1 font-size14"
							onClick={ this.onAddNew }>{ this.getButtonText() }
						</button>
					</div>
					<div className="font-size12 mb-3">{ Strings.add_property_info }</div>
				</>
			</ListGridDataView>
		</>;
	}

	private getTitle() {
		const { searchParams } = this.context;
		if ( searchParams ) {
			const { munNo, munSubNo, landNo, landSubNo } = searchParams;
			if ( !isEmptyStringOrNull( landNo ) && !isEmptyStringOrNull( landSubNo ) ) {
				return `${ Strings.properties } - ${ Strings.land_number } : ${ landNo }-${ landSubNo }`;
			} else if ( !isEmptyStringOrNull( munNo ) && !isEmptyStringOrNull( munSubNo ) ) {
				return `${ Strings.properties } - ${ Strings.municipality_number } : ${ munNo }-${ munSubNo }`;
			}
		}
		return Strings.properties;
	}

	private getButtonText() {
		const { viewType } = this.state;
		if ( viewType === ViewTypes.BUILDINGS ) {
			return Strings.didnt_find_building;
		} else if ( viewType === ViewTypes.VILLAS ) {
			return Strings.didnt_find_villa;
		}
		return Strings.didnt_find_building_villa;
	}

	private loadProperties( landPropertyId: number ) {
		AddPropertyAPI.getLandProperties( landPropertyId, getParticipantID() ).then( resp => {
			this.setState( {
				isLoading: false,
				allProperties: resp,
				properties: resp,
			} );
		} ).catch( e => {
			this.setState( { isLoading: false } );
			showErrorMessage( e );
		} );
	}

	private renderListView = () => {
		const { properties } = this.state;
		return <PropertiesListView
			properties={ properties ? properties : [] }
			onPropertySelect={ this.onPropertySelect }/>;
	};

	private renderGridView = () => {
		const { properties } = this.state;
		return <PropertiesGridView
			properties={ properties ? properties : [] }
			onPropertySelect={ this.onPropertySelect }/>;
	};

	private onChangeViewType( type: number ) {
		const { viewType, allProperties } = this.state;
		if ( viewType !== type ) {
			let properties = allProperties;
			if ( type === ViewTypes.BUILDINGS ) {
				properties = allProperties.filter( item => item.PropertyTypeId === EjariPropertyType.BUILDING );
			} else if ( type === ViewTypes.VILLAS ) {
				properties = allProperties.filter( item => item.PropertyTypeId === EjariPropertyType.VILLA );
			}
			this.setState( {
				viewType: type,
				properties: properties,
			} );
		}
	}

	private onPropertySelect = ( property: ProjectProperty ) => {
		updateProperties( this.state.allProperties );
		if ( property.PropertyTypeId === EjariPropertyType.BUILDING ) {
			updateProperty( property );
			navigateWithInParent( "addproperty", "units" );
		} else if ( property.PropertyTypeId === EjariPropertyType.VILLA ) {
			showEjariDetails( property );
		}
	};

	private onAddNew = () => {
		const { viewType, allProperties } = this.state;
		const pType = ( viewType === ViewTypes.VILLAS ) ? VILLA : BUILDING_AND_UNIT;
		updateProperties( allProperties, ( viewType === ViewTypes.ALL ), pType );
		updateProperty( undefined );
		navigateWithInParent( "addproperty", "new" );
	};
}

export default Properties;
